import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AgentTool } from '@/redux/internal-services/agentsApi';
import { useGetResponseLayoutQuery } from '@/redux/internal-services/toolErrorsApi';
import { layoutProblemText, layoutProblems } from '@/components/ToolErrors/layoutProblems';
import { Badge } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { Chip } from '@/components/UI/Chip';
import { Modal } from '@/components/UI/Modal';
import { cn } from '@/utils/cn';
import { JsonField, Toggle, areaCls, labelCls } from './agentShared';
import { useReportLayoutValidity } from './layoutDrafts';

type Side = 'success' | 'error';
type Layout = Record<string, unknown>;
type DataMode = 'as_returned' | 'template' | 'projection';

const SIDES: { side: Side; labelKey: string }[] = [
  { side: 'success', labelKey: 'platform.toolErrors.onSuccess' },
  { side: 'error', labelKey: 'platform.toolErrors.onFailure' },
];

const DATA_MODES: DataMode[] = ['as_returned', 'template', 'projection'];

const pretty = (v: unknown) => JSON.stringify(v ?? {}, null, 2);
const O = 'platform.toolErrors.override.';

/** One side of the reply layout: shows what it inherits, or this agent's own override. */
const LayoutSide: React.FC<{
  id: string;
  label: string;
  inherited: Layout | undefined;
  inheritedFrom: string;
  override: Layout | undefined;
  required: Record<string, string>;
  onChange: (next: Layout | undefined) => void;
}> = ({ id, label, inherited, inheritedFrom, override, required, onChange }) => {
  const { t } = useTranslation();
  // The user's edits live here; only a valid layout reaches the agent form, and while the text has
  // problems the page is told (layoutDrafts), so Save asks before keeping the last valid layout.
  const [text, setText] = useState(() => pretty(override));
  const sent = useRef(JSON.stringify(override ?? null));
  useEffect(() => {
    // A change that did not come from this box (version restore, discarded draft) replaces its text.
    const now = JSON.stringify(override ?? null);
    if (now !== sent.current) {
      sent.current = now;
      setText(pretty(override));
    }
  }, [override]);
  const send = (next: Layout | undefined) => {
    sent.current = JSON.stringify(next ?? null);
    onChange(next);
  };
  const problems = override ? layoutProblems(text, required) : [];
  useReportLayoutValidity(id, problems.length === 0);

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-ui-text">{label}</span>
          <Badge variant={override ? 'accent' : 'neutral'}>
            {override ? t(`${O}overridden`) : t(`${O}using`, { source: inheritedFrom })}
          </Badge>
        </div>
        {override ? (
          <Button type="button" variant="link" size="xs" onClick={() => send(undefined)}>{t(`${O}useInherited`)}</Button>
        ) : (
          <Button type="button"
            variant="link"
            size="xs"
            onClick={() => {
              // Nothing inherited: start from the keys every layout must keep, never from an empty object.
              // An inherited layout with problems still opens, and its problems show (and are reported).
              const start = inherited ?? { ...required };
              setText(pretty(start));
              send(start);
            }}
          >
            {t(`${O}override`)}
          </Button>
        )}
      </div>
      {override ? (
        <textarea
          aria-label={t(`${O}layoutLabel`, { side: label })}
          className={cn(areaCls, 'font-mono', problems.length > 0 && 'border-danger focus:border-danger')}
          rows={6}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (layoutProblems(e.target.value, required).length === 0) send(JSON.parse(e.target.value));
          }}
        />
      ) : (
        <pre className="no-scrollbar overflow-x-auto rounded-control bg-ui-subtle p-3 font-mono text-xs text-ui-text3">
          {inherited ? pretty(inherited) : t(`${O}noLayout`)}
        </pre>
      )}
      {problems.map((p) => {
        const msg = layoutProblemText(p, t);
        return <p key={msg} className="font-mono text-xs text-danger-text">{msg}</p>;
      })}
    </div>
  );
};

/** Per-agent output override for one attached tool: success text, data shaping, extra fields and reply
 *  layout. Everything here defaults to the shared tool definition; an override affects only this agent. */
export const ToolOutputOverride: React.FC<{
  tool: AgentTool;
  /** The tool's own reply layout from the tool catalogue, if it has one. */
  toolLayout?: { success?: Layout; error?: Layout } | null;
  onUpdate: (patch: Partial<AgentTool>) => void;
}> = ({ tool, toolLayout, onUpdate }) => {
  const { t } = useTranslation();
  const layoutQuery = useGetResponseLayoutQuery();
  const platform = layoutQuery.data?.data;
  const [confirmClear, setConfirmClear] = useState(false);

  // Overriding is "on" the moment any *_override field is non-null — not a separate stored flag.
  const isOverriding =
    tool.output_schema_override != null ||
    tool.apply_data_template_override != null ||
    tool.apply_data_projection_override != null ||
    tool.custom_fields_override != null ||
    tool.response_schema_override != null;

  const enableOverride = () => onUpdate({
    // Seed from the shared values so the admin edits a real starting point, not a blank template. The
    // shared status_message is a documentation sample, not this agent's wording: it is not copied.
    output_schema_override: { ...(tool.output_schema ?? {}), status_message: undefined },
    // One data mode: if the shared tool has both flags on, the template wins (as at runtime).
    apply_data_template_override: tool.apply_data_template ?? false,
    apply_data_projection_override: !tool.apply_data_template && (tool.apply_data_projection ?? false),
    custom_fields_override: tool.custom_fields ?? {},
  });

  const clearOverride = () => onUpdate({
    output_schema_override: null,
    apply_data_template_override: null,
    apply_data_projection_override: null,
    custom_fields_override: null,
    response_schema_override: null,
  });

  const dataMode: DataMode = tool.apply_data_template_override
    ? 'template' : tool.apply_data_projection_override ? 'projection' : 'as_returned';
  const setDataMode = (mode: DataMode) => onUpdate({
    apply_data_template_override: mode === 'template',
    apply_data_projection_override: mode === 'projection',
  });

  const schema = tool.output_schema_override ?? {};
  const ownText = typeof schema.status_message === 'string' ? schema.status_message : undefined;
  const setOwnText = (next: string | undefined) => {
    const rest = { ...schema };
    delete rest.status_message;
    onUpdate({ output_schema_override: next === undefined ? rest : { ...rest, status_message: next } });
  };
  const setLayoutSide = (side: Side, next: Layout | undefined) => {
    const rest = { ...(tool.response_schema_override ?? {}) };
    if (next) rest[side] = next;
    else delete rest[side];
    onUpdate({ response_schema_override: Object.keys(rest).length ? rest : null });
  };
  const toolName = tool.name || tool.tool_name || tool.tool_id;

  return (
    <div className="space-y-3 border-t border-ui-border pt-3">
      <label className="flex cursor-pointer items-center gap-2.5 text-sm text-ui-text">
        <Toggle on={isOverriding} onChange={() => (isOverriding ? setConfirmClear(true) : enableOverride())} />
        {t(`${O}toggle`)}
      </label>
      <p className="-mt-2 ml-9 text-xs text-ui-text3">{t(isOverriding ? `${O}onHint` : `${O}offHint`)}</p>

      {isOverriding && (
        <div className="ml-9 space-y-4">
          <section className="space-y-2">
            <p className="eyebrow">{t(`${O}replyText`)}</p>
            {ownText === undefined ? (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-ui-text2">{t(`${O}replyTextCatalogue`)}</p>
                <Button type="button" variant="link" size="xs" onClick={() => setOwnText('')}>{t(`${O}writeOwnText`)}</Button>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs text-ui-text2">{t(`${O}ownTextHint`)}</p>
                  <Button type="button" variant="link" size="xs" onClick={() => setOwnText(undefined)}>{t(`${O}useCatalogueText`)}</Button>
                </div>
                <textarea
                  aria-label={t(`${O}ownTextLabel`)}
                  className={cn(areaCls, 'font-mono')}
                  rows={2}
                  value={ownText}
                  onChange={(e) => setOwnText(e.target.value)}
                />
              </div>
            )}
          </section>

          <section className="space-y-2">
            <p className="eyebrow">{t(`${O}dataShaping`)}</p>
            <div>
              <label className={labelCls}>{t(`${O}dataSchema`)}</label>
              <JsonField
                value={schema.data ?? {}}
                onChange={(v) => onUpdate({ output_schema_override: { ...schema, data: v } })}
                rows={4}
                placeholder='{"total": {{ total | tojson }}, "items": {{ items | tojson }}}'
              />
            </div>
            <div role="radiogroup" aria-label={t(`${O}dataModeLabel`)} className="flex flex-wrap gap-2">
              {DATA_MODES.map((mode) => (
                <Chip key={mode} role="radio" aria-checked={dataMode === mode} selected={dataMode === mode} onClick={() => setDataMode(mode)}>
                  {t(`${O}dataMode_${mode}`)}
                </Chip>
              ))}
            </div>
          </section>

          <section className="space-y-1">
            <p className="eyebrow">{t(`${O}extraFields`)}</p>
            <JsonField
              value={tool.custom_fields_override}
              onChange={(v) => onUpdate({ custom_fields_override: v as Record<string, string> })}
              rows={2}
              placeholder='{"llm_instruction": "…"}'
            />
            <p className="text-xs text-ui-text3">{t(`${O}extraFieldsHint`)}</p>
          </section>

          <section className="space-y-3">
            <p className="eyebrow">{t(`${O}replyLayout`)}</p>
            {platform ? (
              <>
                {SIDES.map(({ side, labelKey }) => (
                  <LayoutSide
                    key={side}
                    id={`${tool.tool_id}:${side}`}
                    label={t(labelKey)}
                    inherited={toolLayout?.[side] ?? platform[side]}
                    inheritedFrom={t(toolLayout?.[side] ? `${O}fromTool` : `${O}fromPlatform`)}
                    override={tool.response_schema_override?.[side]}
                    required={platform.required_keys[side]}
                    onChange={(next) => setLayoutSide(side, next)}
                  />
                ))}
                <div className="flex flex-wrap items-center gap-1.5 text-caption text-ui-text2">
                  <span>{t('platform.toolErrors.layoutPlaceholders')}</span>
                  {platform.placeholders.map((p) => <Badge key={p} variant="neutral" className="font-mono">{`{{ ${p} }}`}</Badge>)}
                  {(platform.keywords ?? []).map((k) => <Badge key={k} variant="accent" className="font-mono">{k}</Badge>)}
                </div>
              </>
            ) : (
              <p className="text-xs text-ui-text3">
                {layoutQuery.isError ? t(`${O}layoutLoadFailed`) : t('platform.toolErrors.loading')}
              </p>
            )}
          </section>
        </div>
      )}

      {confirmClear && (
        <Modal
          title={t(`${O}clearTitle`)}
          onClose={() => setConfirmClear(false)}
          onSave={() => { clearOverride(); setConfirmClear(false); }}
          saveLabel={t(`${O}clearConfirm`)}
          cancelLabel={t(`${O}cancel`)}
          danger
        >
          <p className="text-sm text-ui-text2">{t(`${O}clearBody`, { tool: toolName })}</p>
        </Modal>
      )}
    </div>
  );
};
